import { Form, Button, Table, Modal } from "react-bootstrap";
import { useEffect, useState, useMemo, useCallback } from "react";
import { useQuery, useMutation } from "react-query";
import queryString from "query-string";
import { toast } from "react-toastify";
import { cloneDeep, isEmpty, uniqBy } from "lodash";
import Select from "react-select";

import "../assets/scss/reports/cashbook.scss";
import {
  paginationOptions,
  defaultSelectValue,
  fetchActionsUtil,
} from "../utils/helpers";
import { useAuth } from "../hooks/useAuth";
import ReactPaginate from "react-paginate";
import useDebounce from "../utils/hooks";
import ConvertQuantity from "./utils/ConvertQuantity";
import { scrollToTop } from "../utils/helpers";
import ConfirmDialog from "./ConfirmDialogue";
import ModalLoader from "./utils/ModalLoader";
import NumberCustomInput from "./utils/NumberCustomInput";
import MagnifyIcon from "mdi-react/MagnifyIcon";
import { format } from "date-fns";
import BatchSelectorModal from "./Inventory/BatchSelectorModal";

const initialFilterParams = {
  page: 1,
  limit: 100,
};

export default function ApproveStockCountAtDate({
  showApproveStockCountAtDate,
  setShowApproveStockCountAtDate,
  StockCount_ID,
  refetch,
}) {
  const { backendUrl, token } = useAuth();

  const [selectedData, setSelectedData] = useState([]);
  const [stockCountDate, setStockCountDate] = useState(null);
  const [showBatchSelectorModal, setShowBatchSelectorModal] = useState(false);
  const [activeBatchItem, setActiveBatchItem] = useState(null);

  const [queryParams, setQueryParams] = useState({ ...initialFilterParams });
  const [filterParams, setFilterParams] = useState({
    ...queryParams,
    ...initialFilterParams,
  });

  const debouncedFilterParams = useDebounce(filterParams, 500);
  useEffect(() => {
    setQueryParams(q => ({ ...q, ...debouncedFilterParams }));
  }, [debouncedFilterParams]);

  // ── Load existing stock count (date + counted items with batches) ──────────
  const fetchStockCountRecord = async () => {
    let response = await fetch(
      `${backendUrl}/api/stock/items/${StockCount_ID}`,
      {
        method: "GET",
        headers: {
          Accept: "Application/json",
          "Content-Type": "Application/json",
        },
        credentials: "include",
      }
    );
    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }
    const { data } = await response.json();
    // Mark all counted items as selected by default
    setSelectedData(
      (data.stockCountItems || []).map(el => ({ ...el, selected: true }))
    );
    if (data.stockCountDate) setStockCountDate(data.stockCountDate);
    return data;
  };

  const stockByIdQuery = useQuery(
    ["STOCK_COUNT_AT_DATE_APPROVE_BY_ID", StockCount_ID],
    fetchStockCountRecord,
    { enabled: !!StockCount_ID }
  );

  // ── Inventory snapshot as of the stock count date ─────────────────────────
  const fetchItems = async queryParams => {
    let finalParams = { ...queryParams };
    if (stockCountDate) {
      const d = format(new Date(stockCountDate), "yyyy-MM-dd");
      finalParams = { ...finalParams, startDate: d, endDate: d };
    }

    let response = await fetch(
      `${backendUrl}/api/reports/stock-records?&${queryString.stringify(
        finalParams
      )}`,
      {
        method: "GET",
        headers: {
          Accept: "Application/json",
          "Content-Type": "Application/json",
        },
        credentials: "include",
      }
    );
    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }
    const { data } = await response.json();

    data.product = [
      { label: "All", value: "" },
      ...data.product.map(el => ({
        label: el.Product_Name,
        value: el.Product_Name,
      })),
    ];
    data.category = [
      { label: "All", value: "" },
      ...data.category.map(el => ({
        label: el.Cat_Name,
        value: el.Cat_Name,
      })),
    ];

    // G.A. added uniq some times db is connected to more than one company
    data.items = uniqBy(
      data.inventory.map(el => ({
        ...el,
        Quantity: Number(el.ClosingQty),
      })),
      "Bar_Code"
    );

    delete data.inventory;
    return data;
  };

  const { data = { count: 0, items: [] } } = useQuery(
    ["STOCK_COUNT_AT_DATE_APPROVE_PRODUCTS", queryParams, stockCountDate],
    () => fetchItems(queryParams),
    {
      keepPreviousData: true,
      enabled: !!stockCountDate,
    }
  );

  // ── Mutations ─────────────────────────────────────────────────────────────
  const approveStockCountAtDate = useMutation(
    payload =>
      fetchActionsUtil(
        `${backendUrl}/api/stock/at-date/approve`,
        "POST",
        token,
        payload
      ),
    {
      onSuccess: ({ message }) => {
        toast.success(message);
        if (refetch) refetch();
        setShowApproveStockCountAtDate(false);
      },
      onError: () => {
        toast.error(`Unable to perform action`);
      },
    }
  );

  const saveItems = async () => {
    const itemsMissingBatches = selectedData.filter(
      el =>
        el.QtyCounted &&
        Number(el.QtyCounted) > 0 &&
        (!el.batches || el.batches.length === 0)
    );
    if (itemsMissingBatches.length > 0) {
      return toast.error(
        `Please select batches for: ${itemsMissingBatches
          .map(el => el.Item_Name || el.Bar_Code)
          .join(", ")}`
      );
    }

    const payload = {
      StockCount_ID,
      stockCountDate,
      items: selectedData.map(el => ({
        ...el,
        QtyCounted: el?.QtyCounted ? el?.QtyCounted : 0,
      })),
    };

    if (
      await ConfirmDialog({
        title: `Approve Stock Count`,
        description: `Are you sure you want to proceed?`,
      })
    ) {
      approveStockCountAtDate.mutate(payload);
    }
  };

  // ── Item handlers ─────────────────────────────────────────────────────────
  const onChangeQtyCounted = useCallback(
    ({ QtyCounted, Bar_Code, Quantity, Item_Name, selected }) => {
      setSelectedData(oldData =>
        cloneDeep(
          oldData
            .filter(obj => obj.Bar_Code !== Bar_Code)
            .concat({
              Bar_Code,
              QtyCounted,
              QtyInStock: Quantity,
              Item_Name,
              selected,
            })
        )
      );
    },
    []
  );

  const isChecked = Bar_Code => {
    const found = selectedData.find(el => el.Bar_Code === Bar_Code);
    if (found) return Boolean(found.selected);
    return false;
  };

  const debouncedSelectedData = useDebounce(selectedData, 500);

  const isEqualClass = useMemo(() => {
    return Object.assign(
      {},
      ...debouncedSelectedData.map(item => {
        const found = debouncedSelectedData.find(
          el => el.Bar_Code === item.Bar_Code
        );
        if (isEmpty(found)) return { [item.Bar_Code]: "" };
        if (Boolean(found.QtyCounted == found.QtyInStock))
          return { [item.Bar_Code]: "correct" };
        return { [item.Bar_Code]: "wrong" };
      })
    );
  }, [debouncedSelectedData]);

  // ── Batch selection ───────────────────────────────────────────────────────
  const handleSelectBatch = ({
    Bar_Code,
    Quantity,
    isDeduction,
    quantityDifference,
  }) => {
    if (!Quantity || Number(Quantity) <= 0) {
      return toast.error("Please enter a Quantity Counted first");
    }
    setActiveBatchItem({ Bar_Code, Quantity, isDeduction, quantityDifference });
    setShowBatchSelectorModal(true);
  };

  const handleBatchSelected = ({ batches }) => {
    // console.log(batches);

    setShowBatchSelectorModal(false);
    if (!batches.length) {
      setActiveBatchItem(null);
      return;
    }
    setSelectedData(oldData =>
      cloneDeep(
        oldData.map(el =>
          el.Bar_Code === activeBatchItem.Bar_Code ? { ...el, batches } : el
        )
      )
    );
    setActiveBatchItem(null);
  };

  const handleFilterParamsChange = e => {
    setFilterParams({
      ...filterParams,
      [e.target.name]:
        e.target.type === "checkbox" ? e.target.checked : e.target.value,
    });
  };

  const handleSearchQueryChange = e => {
    setQueryParams({ ...queryParams, [e.target.name]: e.target.value });
  };

  return (
    <Modal
      show={showApproveStockCountAtDate}
      onHide={() => setShowApproveStockCountAtDate(false)}
      dialogClassName="new-entity-modal"
      backdropClassName="global-backdrop"
      centered
      animation={false}
      enforceFocus={false}
      backdrop="static"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <h1>
            Approve Stock Count
            {stockCountDate
              ? ` — ${format(new Date(stockCountDate), "dd MMM, yyyy")}`
              : ""}
          </h1>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="content-body">
          <header className="text-nowrap d-flex align-items-center justify-content-between gap-3 flex-wrap mb-3">
            <div className="d-flex gap-2 align-items-center flex-grow">
              <div className="global-search-area">
                <MagnifyIcon />
                <input
                  className="form-control search-input"
                  name="itemId"
                  value={filterParams?.itemId}
                  onChange={e => handleFilterParamsChange(e)}
                  placeholder="Search by Item Code..."
                  autoFocus
                  autoComplete="off"
                  type="text"
                />
              </div>

              <Select
                classNamePrefix="form-select"
                menuPosition="fixed"
                menuPlacement="auto"
                placeholder="All"
                name="product"
                isSearchable
                key={data?.product}
                onChange={selected =>
                  setFilterParams({ ...filterParams, product: selected.value })
                }
                defaultValue={defaultSelectValue(
                  queryParams?.product,
                  data?.product,
                  { value: "", label: "Product" }
                )}
                options={data?.product || []}
                isClearable
              />

              <Select
                classNamePrefix="form-select"
                menuPosition="fixed"
                className="mx-0"
                menuPlacement="auto"
                placeholder="All"
                name="category"
                isSearchable
                key={data?.category}
                onChange={selected =>
                  setFilterParams({ ...filterParams, category: selected.value })
                }
                defaultValue={defaultSelectValue(
                  queryParams?.category,
                  data?.category,
                  { value: "", label: "Category" }
                )}
                options={data?.category || []}
                isClearable
              />
            </div>

            <div className="d-flex gap-2 align-items-center">
              <Button
                variant="primary"
                onClick={saveItems}
                disabled={approveStockCountAtDate.isLoading}
              >
                Approve
              </Button>
            </div>
          </header>

          <div className="table-max mb-3">
            <Table borderless responsive striped className="product-table">
              <thead className="sticky border-bottom">
                <tr>
                  <th />
                  <th>S/N</th>
                  <th>Item Code</th>
                  <th>Item Name</th>
                  <th>
                    QTY in Stock <br />
                    <small>(Base UOM)</small>
                  </th>
                  <th>
                    QTY Counted <br />
                    <small>(Base UOM)</small>
                  </th>
                  <th>Overage / Shortage</th>
                  <th>Batch</th>
                </tr>
              </thead>

              <tbody className="position-sticky top-0">
                {data?.items && !stockByIdQuery.isLoading
                  ? data.items.map((el, index) => {
                      const itemData = selectedData.find(
                        id => id.Bar_Code === el.Bar_Code
                      );
                      const batchCount = itemData?.batches?.length || 0;

                      return (
                        <tr
                          key={el?.Bar_Code}
                          className={isEqualClass[(el?.Bar_Code)]}
                        >
                          <td>
                            <Form.Check
                              checked={isChecked(el.Bar_Code)}
                              onChange={e =>
                                onChangeQtyCounted({
                                  QtyCounted: itemData?.QtyCounted,
                                  Bar_Code: el.Bar_Code,
                                  Quantity: el.Quantity,
                                  Item_Name: el.Item_Name,
                                  selected: e.target.checked,
                                })
                              }
                            />
                          </td>
                          <td>{data?.startIndex + index + 1}</td>
                          <td>{el.Bar_Code}</td>
                          <td>{el?.Item_Name}</td>
                          <td className="p-cursor" title={el.Quantity}>
                            <ConvertQuantity
                              quantity={el.Quantity}
                              // desc={el.Item_Desc}
                            />
                          </td>
                          <td>
                            <div
                              style={{
                                width: "11rem",
                                backgroundColor: "#fff",
                              }}
                            >
                              <NumberCustomInput
                                placeholder=""
                                value={itemData?.QtyCounted}
                                onValueChange={value =>
                                  onChangeQtyCounted({
                                    QtyCounted: value,
                                    Bar_Code: el.Bar_Code,
                                    Quantity: el.Quantity,
                                    Item_Name: el.Item_Name,
                                    selected:
                                      itemData?.selected ??
                                      isChecked(el.Bar_Code),
                                  })
                                }
                              />
                            </div>
                          </td>
                          <td>
                            <ConvertQuantity
                              quantity={
                                itemData?.QtyCounted
                                  ? Math.abs(
                                      Number(itemData?.QtyCounted || 0) -
                                        Number(el.Quantity || 0)
                                    )
                                  : ""
                              }
                            />
                          </td>
                          <td>
                            <Button
                              size="sm"
                              className="text-nowrap"
                              variant={
                                batchCount > 0 ? "success" : "light-blue"
                              }
                              onClick={() =>
                                handleSelectBatch({
                                  Quantity: itemData?.QtyCounted,
                                  Bar_Code: el.Bar_Code,
                                  isDeduction:
                                    Number(itemData?.QtyCounted) <
                                    Number(el.Quantity),
                                  quantityDifference: Math.abs(
                                    Number(itemData?.QtyCounted) -
                                      Number(el.Quantity)
                                  ),
                                })
                              }
                            >
                              {batchCount > 0
                                ? `Batches (${batchCount})`
                                : "Select Batch"}
                            </Button>
                          </td>
                        </tr>
                      );
                    })
                  : null}

                {!stockCountDate && (
                  <tr>
                    <td colSpan={7}>
                      <p className="m-5 text-center">Loading stock count…</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          <div className="d-flex justify-content-between px-3 align-items-center pagination">
            <div className="pagination_left d-flex align-items-center gap-3">
              <p className="m-0 p-0">Show</p>
              <select
                value={queryParams.limit}
                name="limit"
                className="form-select"
                onChange={handleSearchQueryChange}
              >
                <option value="10">10 rows</option>
                <option value="20">20 rows</option>
                <option value="30">30 rows</option>
                <option value="40">40 rows</option>
                <option value="50">50 rows</option>
                <option value="100">100 rows</option>
              </select>
            </div>

            <ReactPaginate
              {...paginationOptions}
              pageCount={Math.ceil(data.count / queryParams.limit)}
              marginPagesDisplayed={2}
              pageRangeDisplayed={0}
              onPageChange={({ selected }) => {
                scrollToTop();
                setQueryParams({ ...queryParams, page: selected + 1 });
              }}
              forcePage={queryParams.page - 1}
            />
          </div>
        </div>
      </Modal.Body>

      <ModalLoader show={approveStockCountAtDate.isLoading} />

      {showBatchSelectorModal && activeBatchItem && (
        <BatchSelectorModal
          show={showBatchSelectorModal}
          proceed={handleBatchSelected}
          Bar_Code={activeBatchItem.Bar_Code}
          quantityToHandle={activeBatchItem.quantityDifference}
          loadBatchFromServer={true}
          stockCountDate={stockCountDate}
          isDeduction={activeBatchItem.isDeduction}
          previouslySelectedBatches={
            selectedData.find(el => el.Bar_Code === activeBatchItem.Bar_Code)
              ?.batches || []
          }
        />
      )}
    </Modal>
  );
}
